import type {
  BraceletPatternDto,
  EntityId,
  PatternSummaryDto,
  PatternThumbnailDto,
  Result,
  StoredDraftV1,
  StoredPatternV1,
  StoredSummaryV1,
} from '@miyuki/types';

import { err, ok } from '../shared/result';

import {
  braceletPatternSchema,
  patternSummarySchema,
  storedDraftSchema,
  storedPatternSchema,
  storedSummarySchema,
} from './pattern.schema';

/** Tope de filas de una miniatura: entra en la lista sin pesar. */
export const THUMBNAIL_MAX_ROWS = 24;

/** Motivo por el que una entrada almacenada no se pudo leer. */
export interface DecodeFailure {
  readonly reason: string;
}

function decodeWith<TValue>(
  schema: { safeParse: (input: unknown) => { success: boolean; data?: unknown; error?: unknown } },
  input: unknown,
  extract: (parsed: never) => TValue,
): Result<TValue, DecodeFailure> {
  const parsed = schema.safeParse(input);
  if (!parsed.success) {
    return err({ reason: 'La entrada almacenada no tiene una forma válida' });
  }
  return ok(extract(parsed.data as never));
}

/*
 * Codificar es envolver; decodificar es validar. Decodificar nunca lanza: una
 * entrada ilegible es algo que el adaptador debe poder omitir sin tumbar la
 * aplicación, no una excepción (RF-45).
 */

export function encodePattern(pattern: BraceletPatternDto): StoredPatternV1 {
  return { version: 1, pattern };
}

export function decodePattern(input: unknown): Result<BraceletPatternDto, DecodeFailure> {
  return decodeWith(storedPatternSchema, input, (parsed: StoredPatternV1) => parsed.pattern);
}

export function encodeSummary(summary: PatternSummaryDto): StoredSummaryV1 {
  return { version: 1, summary };
}

export function decodeSummary(input: unknown): Result<PatternSummaryDto, DecodeFailure> {
  return decodeWith(storedSummarySchema, input, (parsed: StoredSummaryV1) => parsed.summary);
}

export function encodeDraft(
  pattern: BraceletPatternDto,
  savedFrom: EntityId | null,
): StoredDraftV1 {
  return { version: 1, pattern, savedFrom, updatedAt: pattern.updatedAt };
}

export function decodeDraft(
  input: unknown,
): Result<{ pattern: BraceletPatternDto; savedFrom: EntityId | null }, DecodeFailure> {
  return decodeWith(storedDraftSchema, input, (parsed: StoredDraftV1) => ({
    pattern: parsed.pattern,
    savedFrom: parsed.savedFrom,
  }));
}

/** Valida un patrón que llega de fuera sin envolver (por ejemplo, de un import). */
export function parsePattern(input: unknown): Result<BraceletPatternDto, DecodeFailure> {
  return decodeWith(braceletPatternSchema, input, (parsed: BraceletPatternDto) => parsed);
}

export function parseSummary(input: unknown): Result<PatternSummaryDto, DecodeFailure> {
  return decodeWith(patternSummarySchema, input, (parsed: PatternSummaryDto) => parsed);
}

/**
 * Rejilla reducida para la lista de diseños.
 *
 * Conserva todas las columnas —nunca pasan de 30— y submuestrea las filas
 * tomando una de cada `k`. Un diseño corto vuelve tal cual.
 */
export function buildThumbnail(
  pattern: BraceletPatternDto,
  maxRows = THUMBNAIL_MAX_ROWS,
): PatternThumbnailDto {
  const total = pattern.rows.length;
  const step = Math.max(1, Math.ceil(total / maxRows));

  const rows = pattern.rows.filter((_row, index) => index % step === 0);

  return {
    columns: pattern.columns,
    rows,
    colors: pattern.palette.colors,
  };
}

/**
 * Resumen que se guarda junto al patrón y alimenta la lista. Se recalcula en
 * cada guardado, de modo que la miniatura nunca queda desactualizada.
 */
export function buildSummary(pattern: BraceletPatternDto): PatternSummaryDto {
  return {
    id: pattern.id,
    name: pattern.name,
    columns: pattern.columns,
    rowCount: pattern.rows.length,
    updatedAt: pattern.updatedAt,
    thumbnail: buildThumbnail(pattern),
    // La lista lo necesita sin cargar el patrón completo (RF-07).
    patternType: pattern.patternType,
  };
}
